<template>
  <nav
    v-if="!loading"
    class="tw:relative tw:flex tw:items-start tw:w-full tw:justify-start tw:lg:justify-between tw:flex-wrap tw:pt-0 tw:lg:pt-6"
    :class="{ 'tw:border-solid tw:border-b tw:border-gray-300': product !== 'rate-connect' }">
    <div
      @scroll="updateShadow"
      ref="appMenuContainer"
      class="tw:flex tw:md:w-full tw:overflow-auto tw:justify-start tw:lg:justify-between tw:lg:items-center tw:lg:w-auto">
      <AppMenuItem
        v-for="item in data"
        :key="item.route"
        :route="item.route"
        :data-testid="item?.testId"
        :id="item?.testId"
        :ref="el => setItemRef(el, item.route)">
        <div v-if="item.withBadge">
          <span
            class="tw:bg-red-700 tw:text-white tw:text-xxs tw:font-bold tw:absolute tw:px-2 tw:py-[2px] tw:rounded-tl-2xl tw:rounded-br-2xl tw:top-0 tw:left-0"
            >{{ $t('rc_smartboost_new_tag') }}</span
          >
        </div>
        <span>{{ $t(`${item.label}`) }}</span>
      </AppMenuItem>
    </div>

    <div v-if="hasShadow && !isScrolledToRight" class="scroll-shadow" />
  </nav>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import AppMenuItem from '@/components/appMenu/AppMenuItem.vue'
import { useIsMobile } from '@/composables/useIsMobile'

const route = useRoute()
const { isMobile } = useIsMobile()
const appMenuContainer = ref(null)
const itemRefs = ref([])
const hasShadow = ref(false)
const isScrolledToRight = ref(false)
const props = defineProps({
  product: String,
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const setItemRef = (el, route) => {
  if (el) {
    itemRefs.value[route] = el.$el ?? el
  } else {
    delete itemRefs.value[route]
  }
}

const scrollToCenter = async route => {
  await nextTick()

  if (!isMobile.value) return

  const container = appMenuContainer.value
  const item = itemRefs.value[route]

  if (!container || !item) return

  const containerWidth = container.offsetWidth
  const itemLeft = item.offsetLeft
  const itemWidth = item.offsetWidth

  const rawPosition = itemLeft - containerWidth / 2 + itemWidth / 2
  const max = container.scrollWidth - containerWidth
  const clamped = Math.min(Math.max(rawPosition, 0), max)

  container.scrollTo({
    left: clamped,
    behavior: 'smooth',
  })
  updateShadow()
}

watch(
  () => [route.path, props.loading, props.data.length],
  ([newPath, loading]) => {
    if (!loading) scrollToCenter(newPath)
  },
  { immediate: true }
)

const updateShadow = () => {
  if (appMenuContainer.value) {
    hasShadow.value = appMenuContainer.value.scrollWidth > appMenuContainer.value.clientWidth
    isScrolledToRight.value =
      Math.round(appMenuContainer.value.scrollLeft) + Math.round(appMenuContainer.value.clientWidth) + 1 >=
      Math.round(appMenuContainer.value.scrollWidth)
  }
}

onMounted(() => {
  updateShadow()
  window.addEventListener('resize', updateShadow)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateShadow)
})
</script>
<style scoped>
.scroll-shadow {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 120px;
  height: 44px;
  background: linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1)); /* White shadow */
  pointer-events: none;
}
</style>
